import React, { useState } from 'react';
import { useProjects } from '../hooks/useProjects';
import { useCategoryReport, useTimeReport } from '../hooks/useReports';
import { usePermissions } from '../hooks/usePermissions';
import CategoryReport from '../components/reports/CategoryReport';
import TimeReport from '../components/reports/TimeReport';
import ProjectReport from '../components/reports/ProjectReport';
import UserReport from '../components/reports/UserReport';
import ReportFilters from '../components/reports/ReportFilters';
import ExportButtons from '../components/reports/ExportButtons';
import LoadingSpinner from '../components/common/LoadingSpinner';
import type { ReportFilter } from '../types';

type ReportType = 'category' | 'time' | 'project' | 'user';
type PeriodType = 'week' | 'biweekly' | 'month' | 'custom';

const Reports: React.FC = () => {
  const [reportType, setReportType] = useState<ReportType>('category');
  const [periodType, setPeriodType] = useState<PeriodType>('month');
  const [filters, setFilters] = useState<ReportFilter>({});
  const { isColaborador } = usePermissions();

  const { data: projects, isLoading: projectsLoading } = useProjects();
  const {
    data: categoryReportData,
    isLoading: categoryLoading,
    error: categoryError,
  } = useCategoryReport(reportType === 'category' ? filters : undefined);
  const {
    data: timeReportData,
    isLoading: timeLoading,
    error: timeError,
  } = useTimeReport(
    reportType === 'time' ? periodType : '',
    reportType === 'time' ? filters : undefined
  );

  const isLoading = projectsLoading || categoryLoading || timeLoading;
  const error = (categoryError || timeError) as Error | null;

  const projectsList =
    projects?.map((p) => ({
      projectId: p.projectId,
      name: p.name,
    })) || [];

  const selectedProject = projects?.find((p) => p.projectId === filters.projectId);

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reportes</h1>
        </div>
        {!isLoading && reportType !== 'project' && reportType !== 'user' && (reportType === 'category' ? categoryReportData : timeReportData) && (
          <ExportButtons reportType={reportType} filters={filters} />
        )}
      </div>

      {/* Report Type Selector */}
      <div className="bg-white p-4 lg:p-6 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Tipo de Reporte:</span>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setReportType('category')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                reportType === 'category'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Por Categoría
            </button>
            <button
              onClick={() => setReportType('time')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                reportType === 'time'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Por Período de Tiempo
            </button>
            <button
              onClick={() => setReportType('project')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                reportType === 'project'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Por Proyecto
            </button>
            {!isColaborador && (
              <button
                onClick={() => setReportType('user')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  reportType === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Por Usuario
              </button>
            )}
          </div>

          {reportType === 'time' && (
            <>
              <span className="text-sm font-medium text-gray-700 sm:ml-4">
                Período:
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setPeriodType('week')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    periodType === 'week'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Semanal
                </button>
                <button
                  onClick={() => setPeriodType('biweekly')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    periodType === 'biweekly'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Quincenal
                </button>
                <button
                  onClick={() => setPeriodType('month')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    periodType === 'month'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Mensual
                </button>
                <button
                  onClick={() => setPeriodType('custom')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    periodType === 'custom'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Personalizado
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      {!isLoading && reportType !== 'project' && reportType !== 'user' && (
        <ReportFilters
          filters={filters}
          onFiltersChange={setFilters}
          projects={projectsList}
          showDateRange={reportType === 'category' || periodType === 'custom'}
        />
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Error al cargar el reporte
          </h3>
          <p className="text-red-600">
            {error instanceof Error ? error.message : 'Ocurrió un error inesperado'}
          </p>
        </div>
      )}

      {/* Report Content */}
      {!isLoading && !error && (
        <>
          {reportType === 'category' && categoryReportData && (
            <CategoryReport
              categories={categoryReportData.categories}
              grandTotal={categoryReportData.grandTotal}
              totalExpenses={categoryReportData.totalExpenses}
              projectName={selectedProject?.name}
              startDate={filters.startDate}
              endDate={filters.endDate}
            />
          )}

          {reportType === 'time' && timeReportData && (
            <TimeReport
              periods={timeReportData.periods}
              periodType={periodType}
              grandTotal={timeReportData.grandTotal}
              totalExpenses={timeReportData.totalExpenses}
              projectName={selectedProject?.name}
            />
          )}

          {reportType === 'project' && projects && (
            <ProjectReport projects={projects} />
          )}

          {reportType === 'user' && (
            <UserReport />
          )}
        </>
      )}

      {/* Empty State */}
      {!isLoading &&
        !error &&
        reportType !== 'project' &&
        reportType !== 'user' &&
        ((reportType === 'category' &&
          categoryReportData &&
          categoryReportData.categories.length === 0) ||
          (reportType === 'time' &&
            timeReportData &&
            timeReportData.periods.length === 0)) && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              No hay datos para mostrar
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              No se encontraron gastos con los filtros seleccionados.
            </p>
          </div>
        )}
    </div>
  );
};

export default Reports;
